import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import MuiDrawer from '@mui/material/Drawer';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { type ReactNode, useId } from 'react';

import { cssVar } from '../../theme/tokens';
import { IconButton } from './IconButton';

/** Widths: 384, 480 and 624 px (multiples of the 48 px step) and the 1040 px panel (never wider than the screen). */
const WIDTH = {
  sm: `calc(${cssVar('space-12')} * 8)`,
  md: `calc(${cssVar('space-12')} * 10)`,
  lg: `calc(${cssVar('space-12')} * 13)`,
  xl: cssVar('panel-xl'),
} as const;

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  /** Footer buttons (right-aligned). */
  actions?: ReactNode;
  size?: keyof typeof WIDTH;
  anchor?: 'right' | 'left';
  children?: ReactNode;
}

/** A side sheet for details and secondary forms; Escape and the backdrop close it. */
export function Drawer({
  open,
  onClose,
  title,
  actions,
  size = 'md',
  anchor = 'right',
  children,
}: DrawerProps) {
  const titleId = useId();
  return (
    <MuiDrawer
      anchor={anchor}
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          role: 'dialog',
          'aria-labelledby': titleId,
          sx: {
            width: `min(100vw, ${WIDTH[size]})`,
            bgcolor: cssVar('surface-raised'),
            backgroundImage: 'none',
          },
        },
      }}
    >
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          gap: 2,
          px: 5,
          py: 3,
          borderBottom: `1px solid ${cssVar('border')}`,
        }}
      >
        <Typography id={titleId} variant="h4" component="h2" sx={{ flex: 1, minWidth: 0 }}>
          {title}
        </Typography>
        <IconButton label="Close" tooltip={false} onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Stack>
      <Box sx={{ flex: 1, overflowY: 'auto', p: 5 }}>{children}</Box>
      {actions ? (
        <Stack
          direction="row"
          sx={{
            justifyContent: 'flex-end',
            gap: 2,
            px: 5,
            py: 4,
            borderTop: `1px solid ${cssVar('border')}`,
          }}
        >
          {actions}
        </Stack>
      ) : null}
    </MuiDrawer>
  );
}
